<template>
  <div
    :class="[
      'selectable-card',
      { 'selectable-card--boxed': boxed, 'selectable-card--selected': selected, 'selectable-card--disabled': disabled },
    ]"
  >
    <div class="selectable-card__content">
      <div class="selectable-card__title">
        <slot name="title" />
      </div>

      <div v-if="hasSlotText" class="selectable-card__text">
        <slot name="text" />
      </div>
    </div>

    <div class="selectable-card__input" ref="inputWrapper">
      <slot name="input" />
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { hasSlotContent } from '@components/slots';

export default defineComponent({
  name: 'SelectableCard',

  props: {
    boxed: {
      type: Boolean,
      default: false,
    },

    selected: {
      type: Boolean,
      default: false,
    },

    /**
     * The item should be focussed
     */
    focussed: {
      type: Boolean,
      required: false,
      default: false,
    },

    disabled: {
      type: Boolean,
      default: false,
    },
  },

  computed: {
    hasSlotText() {
      return hasSlotContent(this.$slots.text, {});
    },
  },

  watch: {
    focussed(newValue) {
      if (newValue) {
        (this.$refs.inputWrapper as Element).firstElementChild?.focus();
      }
    },
  },
});
</script>

<style lang="scss">
@use 'settings/colors';
@use 'settings/settings';

.selectable-card {
  display: flex;
  gap: 16px;
  padding: 12px;
  border-radius: settings.$border-radius;
  cursor: pointer;
}

.selectable-card__content {
  flex: 1 1 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.selectable-card__input {
  flex: 0 0 min-content;
  display: flex;
  align-items: center;
}

.selectable-card__title {
  font-size: 14px;
  line-height: 1.6;
  font-weight: 500;
  color: colors.$color-grey-900;
}

.selectable-card__text {
  font-size: 14px;
  line-height: 1.6;
  font-weight: 400;
  color: colors.$color-grey-500;
}

.selectable-card:hover {
  background: colors.$color-grey-100;
}

.selectable-card:focus,
.selectable-card:focus-within {
  outline: none;
  box-shadow: 0 0 0 2px rgba(colors.$color-blue-600, 0.2);
}

.selectable-card.selectable-card--selected {
  background: colors.$color-blue-50;
  border: 1px solid colors.$color-blue-600;

  .selectable-card__title {
    color: colors.$color-blue-600;
  }

  .selectable-card__text {
    color: colors.$color-blue-600;
  }
}

.selectable-card.selectable-card--disabled {
  cursor: not-allowed;

  .selectable-card__title {
    color: colors.$color-grey-400;
  }

  .selectable-card__text {
    color: colors.$color-grey-400;
  }
}

.selectable-card--boxed {
  padding: 16px;
  border: 1px solid colors.$color-grey-200;
}

.selectable-card--boxed.selectable-card--disabled {
  background: colors.$color-grey-50;
}
</style>
